import { FC, ReactElement, useMemo } from 'react';

import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { useToggle } from 'react-use';

import { Picture } from '../../../../../components';
import { Button } from '../../../../../compositions';
import { FormImageOption } from '../../../../../entities/@forms/Form/Form';
import { ImageFormatSize } from '../../../../../entities/Media/Media';
import { useTrans } from '../../../../../hooks';

import './ProductVariantSelector.scss';

interface ProductVariantSelectorProps {
    variants: FormImageOption[];
    currentSlug: string;
    className?: string;
}

export const ProductVariantSelector: FC<ProductVariantSelectorProps> = ({
    variants,
    currentSlug,
    className = '',
}): ReactElement => {
    const trans = useTrans();

    const initialVisibleCount: number = 4;
    const [isExpanded, toggleIsExpanded] = useToggle(false);

    const shouldShowExpandable = variants.length > initialVisibleCount + 1;

    const visibleVariants = useMemo((): FormImageOption[] => {
        if (shouldShowExpandable && !isExpanded) {
            return variants.slice(0, initialVisibleCount);
        }

        return variants;
    }, [isExpanded, variants, shouldShowExpandable]);

    return (
        <div className={`product-variant-selector ${className}`}>
            <p className="product-variant-selector__name">
                <span>
                    {trans('containers.productDetailRenderer.availableIn')}
                </span>

                <span className="product-variant-selector__variants-amount">
                    {trans('containers.productDetailRenderer.variants', { amount: variants.length })}
                </span>
            </p>

            <div className="product-variant-selector__variants-wrapper">
                {visibleVariants.map(variant => {
                    const isCurrentVariant = currentSlug === variant.value;

                    const variantClassNames = classNames('product-variant-selector__variant', {
                        'product-variant-selector__variant--is-active': isCurrentVariant,
                    });

                    return (
                        <Link
                            key={variant.value}
                            title={variant.label}
                            aria-label={variant.label}
                            to={`/${variant.value}`}
                            tabIndex={isCurrentVariant ? -1 : 0}
                            className={variantClassNames}
                        >
                            <Picture
                                {...variant.image}
                                formatOverride={ImageFormatSize.thumbnail}
                                className="product-variant-selector__variant-picture"
                            />
                        </Link>
                    );
                })}

                {shouldShowExpandable && !isExpanded && (
                    <Button
                        text={`+ ${variants.length - initialVisibleCount}`}
                        onClick={toggleIsExpanded}
                        className="product-variant-selector__variant product-variant-selector__variant--expand-button"
                    />
                )}
            </div>

            {shouldShowExpandable && isExpanded && (
                <Button
                    isSmall
                    text={trans('common.showLess')}
                    onClick={toggleIsExpanded}
                    className="product-variant-selector__link stretch"
                />
            )}
        </div>
    );
};
